import _ from "lodash";
import isNil from "lodash/isNil";
import { LANGUAGE_CODE_MAPPER, ROUTES, SHOP_TYPES } from "./constants.jsx";
import { COUNTRY_NAMES_BY_CODE, getCountryName } from "rylak-common/src/address/countryNames";
import { normalizeCountryCode } from "rylak-common/src/address/countryCode";
import currencies from "./currencyCodes";
import queryString from "query-string";
import { getSelectedBrandName } from "./cores/appCore";
import { getBrandName } from "./cores/brandCore";

export function padWithZeros(id, len) {
    id += "";
    while (id.length < len) {
        id = "0" + id;
    }
    return id;
}

export function calculatePercentage(part, total) {
    return part / Math.max(total, 1);
}

export function reverseMap(list, f) {
    if (!list) {
        return list;
    }
    return new Array(list.length).fill(null).map((v, i) => f(list[list.length - i - 1], list.length - i - 1));
}

export function isExternalLink(href) {
    return href?.startsWith("http");
}

export function formatProductVariantName(productVariant) {
    return `${productVariant?.product?.title || productVariant?.merchantSalesPlatformItem?.title} ${productVariant?.title}`;
}

export function formatOrderItemName(orderItem) {
    return formatProductVariantName(orderItem?.merchantSalesPlatformItemVariant || orderItem?.productVariant);
}

export function combineName(firstName, lastName) {
    return (firstName + " " + lastName).trim();
}

//
// // ECOM PLATFORM LINKS
// export function getShopPlatformLogo({ merchantBrand, platform }) {
//     switch (merchantBrand?.shopType || platform) {
//         case SHOP_TYPES.SHOPIFY:
//             return "/static/images/storeLogos/shopify.svg";
//         case SHOP_TYPES.CENTRA:
//             return "/static/images/storeLogos/centra.svg";
//         case SHOP_TYPES.JETSHOP:
//             return "/static/images/storeLogos/jetshop.png";
//         case SHOP_TYPES.WOOCOMMERCE:
//             return "/static/images/storeLogos/woocommerce.svg";
//         case SHOP_TYPES.BRINK:
//             return "/static/images/storeLogos/brink.svg";
//         case SHOP_TYPES.BRINK_RETAIL:
//             return "/static/images/storeLogos/brink_retail.svg";
//         case SHOP_TYPES.NAKD:
//             return "/static/images/storeLogos/nakd.svg";
//         case SHOP_TYPES.MAGENTO:
//             return "/static/images/storeLogos/magento.svg";
//         case SHOP_TYPES.RAPUNZEL:
//             return "/static/images/storeLogos/rapunzel.jpg";
//         case SHOP_TYPES.SALESFORCE_CORE:
//             return "/static/images/storeLogos/salesforce.svg";
//         case SHOP_TYPES.DEMO:
//             return "/static/logos/logo-ingrid-black-dot.svg";
//         default:
//             return "/static/images/storeLogos/unknown.png";
//     }
// }

// export function getShopPlatformName({ selectedBrandData, merchantBrand = selectedBrandData, platform }) {
//     switch (merchantBrand?.shopType || platform) {
//         case SHOP_TYPES.SHOPIFY:
//             return "Shopify";
//         case SHOP_TYPES.CENTRA:
//             return "Centra";
//         case SHOP_TYPES.JETSHOP:
//             return "Norce";
//         case SHOP_TYPES.WOOCOMMERCE:
//             return "WooCommerce";
//         case SHOP_TYPES.BRINK:
//             return "Brink";
//         case SHOP_TYPES.BRINK_RETAIL:
//             return "Brink Retail";
//         case SHOP_TYPES.DEMO:
//             return "Demo";
//         case SHOP_TYPES.RAPUNZEL:
//             return "Rapunzel";
//         case SHOP_TYPES.MAGENTO:
//             return "Magento";
//         case SHOP_TYPES.NAKD:
//             return "Nakd";
//         case SHOP_TYPES.SALESFORCE_CORE:
//             return "Salesforce core";
//         default:
//             return "Unknown shop type";
//     }
// }

export function getQRUrl({ order, returnId, initialProductId, appProps } = {}) {
    let basePath = getOrderPackingInstructionsUrl({ order, appProps, returnId });
    if (initialProductId) {
        basePath += `&initialProductId=${initialProductId}`;
    }
    return basePath;
}

const locales = "sv-SE";

export function formatAmountWithCurrency(amount, { style = "currency", currencyCode, currency = currencyCode, minimumFractionDigits = 0, maximumFractionDigits = 0 } = {}) {
    if (amount === undefined || amount === null) {
        return null;
    }
    if (style === "currency" && !currency && !currencyCode) {
        return amount;
    }
    return new Intl.NumberFormat(locales, {
        // currencyDisplay: "narrowSymbol",
        style,
        currency,
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(amount);
}

export function formatAmountWithComma(amount) {
    return new Intl.NumberFormat("en").format(amount);
}

export function formatPercentage(amount, options) {
    return formatAmountWithCurrency(amount, {
        style: "percent",
        maximumFractionDigits: 1,
        ...options,
    })?.replace(" ", "");
}

export function getCurrencySymbol(currency) {
    return (0)
        .toLocaleString(locales, {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
        .replace(/\d/g, "")
        .trim();
}

export function numberFormatterWithLetters(amount) {
    function nFormatter(num, digits) {
        const lookup = [
            { value: 1, symbol: "" },
            { value: 1e3, symbol: "K" },
            { value: 1e6, symbol: "M" },
            { value: 1e9, symbol: "G" },
            { value: 1e12, symbol: "T" },
            { value: 1e15, symbol: "P" },
            { value: 1e18, symbol: "E" },
        ];
        const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
        var item = lookup
            .slice()
            .reverse()
            .find(function (item) {
                return num >= item.value;
            });
        return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
    }

    return nFormatter(amount, 1);
}

export function getBrandTurnrUrl({ brandName, appProps, selectedBrandName = getSelectedBrandName(appProps) } = {}) {
    return `${appProps?.system?.returnsBaseUrl}/${brandName || selectedBrandName}`;
}

export function getOrderName(order) {
    return order?.foreignName || order?.foreignNumber || order?.foreignId;
}

export function getOrderShop(order) {
    return order?.shop || order?.merchantBrand;
}

export function getOrderShopName(order) {
    return getBrandName(getOrderShop(order));
}

export function getOrderStatusUrl({ order, appProps, returnId }) {
    return `${getBrandTurnrUrl({
        brandName: order?.merchantBrand?.name,
        appProps,
    })}/status/${order?.foreignId}?${queryString.stringify({
        email: order?.entityAddress?.email,
        return: returnId,
    })}`;
}

export function getOrderPackingInstructionsUrl({ order, appProps, returnId }) {
    return `${getBrandTurnrUrl({
        brandName: order?.merchantBrand?.name,
        appProps,
    })}/packing-instructions/${order?.foreignId}?${queryString.stringify({
        email: order?.entityAddress?.email,
        return: returnId,
    })}`;
}

export function isValueEmpty(v) {
    return v === "" || v === null || v === undefined || v === false;
}

export function countTrailingZeros(v) {
    if (v === 0) {
        return 1;
    }
    const array = ("" + v).split("0");
    let count = 0;
    while (count < array.length) {
        if (array[array.length - 1 - count] === "") {
            count++;
        } else {
            break;
        }
    }
    return count;
}

export function isOpenNewTabKeyUsed(e) {
    return e.metaKey || e.ctrlKey || e.button === 1;
}

export function openPDFTab(pdf) {
    let pdfWindow = window.open("");
    pdfWindow.document.write(
        "<iframe style='position: fixed; top: 0; bottom:0; right: 0; border:none; margin: 0; padding: 0; overflow: hidden; width:100%; height:100%;' src='data:application/pdf;base64, " +
            encodeURI(pdf) +
            "'></iframe>",
    );
}

export function smartHrefOnClick(e, router, href) {
    if (isOpenNewTabKeyUsed(e)) {
        window.open(href, "_blank");
    } else {
        return router.push(href);
    }
}

export function isNilOrEmptyString(v) {
    return isNil(v) || v === "";
}

export function isNilOrZero(v) {
    return isNil(v) || v === 0;
}

export function or(...v) {
    return v?.find((s) => !isNilOrEmptyString(s));
}

const countryGroups = { EU: "European Union" };

export function getCountryCodesIncludingCountryGroups() {
    return Object.keys(countryGroups).concat(getCountryCodes());
}

export function getCountryCodes() {
    return Object.keys(COUNTRY_NAMES_BY_CODE);
}

export function getCountryGroups() {
    return Object.keys(countryGroups);
}

export function getCurrencies() {
    return Object.keys(currencies);
}

export function currencyCodeToName(currencyCode) {
    return currencies[currencyCode]?.name || "";
}

export function currencyCodeToCountryCode(currencyCode) {
    return currencies[currencyCode]?.countryCode || "";
}

export function countryCodeToCountryName(countryCode) {
    // Normalised once so all three lookups share the tolerance -- getCountryName trims and
    // upper-cases internally, and " eu " should not resolve differently from " se ".
    const code = normalizeCountryCode(countryCode);
    if (code === "GLOBAL") {
        return "Global";
    }
    return getCountryName(code) || countryGroups[code] || "";
}

function getShopTypeSortValue(shopType) {
    return shopType === SHOP_TYPES.DEMO ? 2 : 1;
}

export function compareShopTypes(shopType1, shopType2) {
    const a = getShopTypeSortValue(shopType1);
    const b = getShopTypeSortValue(shopType2);
    return a < b ? -1 : b < a ? 1 : 0;
}

export function isStartupGuideDone(props) {
    return getStartupGuideSteps(props).filter((step) => !step.done).length === 0;
}

// export function getStartupGuideStepSetUrl(props) {
//     return {
//         title: "Set your store URL",
//         content: "We need you to enter your website / store URL so that we can smoothly guide your customers back to your website",
//         buttonText: "Design Settings",
//         pagePath: "/settings",
//         done: !!getBrandHomePage(props),
//     }
// }

export function getReturnConfigurationStep(props) {
    return {
        title: "Prices & Features",
        content: "What return features do you want to activate for your customers and for what price?",
        backgroundImage:
            "data:image/jpeg;base64,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",
        buttonText: "Prices & Features",
        pagePath: ROUTES.SETTINGS_PRICE_SETUPS,
        done: props?.brandService?.data?.guideData?.returnConfigurationDone,
    };
}

export function getStartupGuideSteps(props) {
    // Store website URL (Settings)
    // Shipment integrations. Man ska kunna välja skriva in en annan integrations. Behöver hjälp från oss blablabla
    // Shop addresses (Address to shop inventory etc). Vi har ett API men behöver sida. Kanske hör ihop med price list
    // Price list. Vi har ett API. Behöver en sida.
    // Warehouse integration (Alternativt använda "Turnr warehouse"). Vilket betyder att dom inte hare nån warehouse. Också om man vill ha en annan warehouse integration ett alternativ för de.

    // Payment Plan (Försök göra optional)
    // Gör brand-logo src optional
    // Payment Plan. Ska även synas när man loggar in utanför shopifies sida.
    // Titta på att korta ner listan till vänster.

    return [
        //     {
        //     title: "Select Payment Plan",
        //     content: "Go ahead and select the payment plan that best fits your business",
        //     buttonText: "Payment Plan",
        //     pagePath: "/payment-plan",
        //     done: false,
        // },
        // getStartupGuideStepSetUrl(props),
        {
            title: "Shipment Integration",
            content: "Connect one or multiple shipment services for your customers to choose between",
            buttonText: "Shipment Integrations",
            backgroundImage:
                "https://i.guim.co.uk/img/media/3ed1a62c989d9edfe6232dad1dfe49fa7f25aaf9/0_450_4500_2700/master/4500.jpg?width=1200&height=1200&quality=85&auto=format&fit=crop&s=ce20365626a27d9d3aee9bfe51249f69",
            pagePath: ROUTES.INTEGRATIONS_SHIPMENT,
            done: props?.brandService?.data?.guideData?.shipmentIntegrationDone,
        },
        getReturnConfigurationStep(props),
        {
            title: "Warehouse Integration",
            content: "Either configure your warehouse service or use a pre-configured warehouse service",
            backgroundImage: "https://media.istockphoto.com/id/1208067405/photo/3d-warehouse-building.jpg?s=170667a&w=0&k=20&c=Zt7-BL4pHbEX98Fcrah89z3CzrrjPyQ-2312y_iWBLc=",
            buttonText: "Warehouse Integrations",
            pagePath: ROUTES.INTEGRATIONS_WAREHOUSE,
            done: props?.brandService?.data?.guideData?.wmsIntegrationDone,
        },
    ];
}

export function getNextStartupStep(props) {
    return getStartupGuideSteps(props).find((step) => !step.done);
}

export function getStartupPercentageDone(props) {
    const steps = getStartupGuideSteps(props);
    const doneSteps = steps.filter((step) => step.done);
    return Math.round((doneSteps.length / steps.length) * 100);
}

export function isBetween(n, low, high) {
    return n >= low && n <= high;
}

// ChatGPT
export function sorted(arr, cmp) {
    return arr?.slice()?.sort(cmp);
}

// ChatGPT
export function compareAll(param1, param2, ...comparators) {
    for (const comparator of comparators) {
        const result = comparator(param1, param2);
        if (result !== 0) {
            return result;
        }
    }
    return 0;
}

export function languageCodeToCountryCode(languageCode) {
    const result = LANGUAGE_CODE_MAPPER[languageCode]?.countryCode;
    if (result) {
        return result;
    }
    console.warn("Missing language to country code translation for: ", languageCode);
    return languageCode;
}

export function languageCodeToName(languageCode) {
    return LANGUAGE_CODE_MAPPER[languageCode]?.name;
}

export function sortLanguageCodes(languageCodes) {
    return languageCodes?.sort((c1, c2) => {
        const c1Name = LANGUAGE_CODE_MAPPER[c1]?.name;
        const c2Name = LANGUAGE_CODE_MAPPER[c2]?.name;
        if (c1Name > c2Name) {
            return 1;
        }
        if (c2Name > c1Name) {
            return -1;
        }
        return 0;
    });
}

export function convertServiceDateToInputDate(date) {
    if (!date) {
        return date;
    }
    return new Date(date).toISOString()?.slice(0, 10);
}

export function convertInputDateToServiceDate(date) {
    return date && new Date(date).toISOString();
}

export function isInputValueChanged(originalValue, value) {
    if (_.isArray(value)) {
        return originalValue?.length !== value?.length || value?.find((v) => !originalValue?.includes(v));
    }
    if (_.isObject(value)) {
        return !_.isEqual(value, originalValue);
    }
    return !(isValueEmpty(value) && isValueEmpty(originalValue)) && originalValue !== value;
}

// Phones/Tablets

export function isIPad({ userAgent }) {
    return /iPad/.test(userAgent);
    // For the adventurous && !window.MSStream)

    // TODO I simply removed this
    // Newer iPad mimics Mac Safari, so checking for iPad is not enough.
    // return windowObject.navigator.maxTouchPoints && windowObject.navigator.maxTouchPoints > 2 && /MacIntel/.test(windowObject.navigator.platform);
}

export function isIPhone({ userAgent, windowObject = {} } = {}) {
    return /iPhone/.test(userAgent) && !windowObject.MSStream;
}

export function isIPod({ userAgent, windowObject = {} } = {}) {
    return /iPod/.test(userAgent) && !windowObject.MSStream;
}

export function isIOS(params) {
    return isIPad(params) || isIPhone(params) || isIPod(params);
}

export function isWindowsPhone({ userAgent } = {}) {
    return /windows phone/i.test(userAgent);
}

export function isAndroid({ userAgent } = {}) {
    // Windows phones can have android in the agent so we must first check that it is not a windows phone
    return !isWindowsPhone({ userAgent }) && /android/i.test(userAgent);
}

export function isPhoneOrTablet(params) {
    return isIOS(params) || isWindowsPhone(params) || isAndroid(params);
}

export function isPhone(params) {
    return isPhoneOrTablet(params) && !isIPad(params);
}

export function filterObject(obj, callback) {
    return Object.fromEntries(Object.entries(obj).filter(([key, val]) => callback(val, key)));
}

export function equalAsStringOrNumber(value1, value2) {
    return "" + value1 === "" + value2;
}

export function includesStringOrNumber(array, value) {
    return array?.includes(Number(value)) || array?.includes("" + value);
}
